/**
 * Krok SEZ1 bod 1 — NA KOLIKA PORTALECH INZERAT BEZI (jeden vypocet).
 *
 * R.B. 29. 10. 2026 (Realman prohlidka, bod 8): „v seznamu nemovitosti chci
 * videt stav exportu — 3/10 a v tooltipu ktere portaly a jak". Tentyz souhrn
 * uz kresli obrazovka Portaly (`routes/portaly.tsx`, souhrn „N z M
 * zveřejněno“). Kdyby si ho seznam nemovitosti napocital sam, rozesly by se
 * — presne jako se v kroku UXP4b roze­sel jmenovatel mezi Portaly a Novym
 * inzeratem (nalez P2-8). Proto je vypocet TADY a obe obrazovky ho volaji.
 *
 * Co je co:
 *  - citatel `published` = publikace, ktera na portalu ZIVE BEZI
 *    (`isPublishedStatus` — tytez dva stavy, ktere maji na Portalech zeleny
 *    odznak),
 *  - jmenovatel `total` = portaly, KAM JDE DNES ZVEREJNIT
 *    (`canPublishToPortal` — napojeny konektor bez partnerske smlouvy),
 *  - `runState` = tri lidske stavy ze zadani (bezi / ceka / chyba) + `off`
 *    pro portal, kde se nic nedeje (nenapojeno, ceka na specifikaci,
 *    vyzaduje partnera, stazeno).
 *
 * Modul je ciste vypocetni (bez `fetch`, bez `Date.now()`), aby sel merit
 * bez prohlizece a bez site.
 */

import { canPublishToPortal, type PortalDescriptor, type SyncCenterCell, type SyncCenterView } from './connectors.js';

/**
 * Krok UXF2 bod 7: „zverejneno" = tytez dva stavy, ktere maji na Portalech
 * zeleny odznak (`badgeClass`). Jedna funkce, aby souhrn „N z M" nerikal nic
 * jineho, nez co ukazuji odznaky pod nim.
 *
 * Do kroku SEZ1 zila v `routes/portaly.tsx`; presunuta sem, protoze ji ted
 * cte i seznam nemovitosti.
 */
export const isPublishedStatus = (status: string): boolean => status === 'PUBLISHED' || status === 'UPDATED';

/** Tri stavy ze zadani SEZ1 + `off` pro portal, kde se nic nedeje. */
export type PortalRunState = 'running' | 'waiting' | 'error' | 'off';

export function portalRunState(status: string | undefined): PortalRunState {
  if (status === undefined) return 'off';
  if (isPublishedStatus(status)) return 'running';
  if (status === 'ERROR') return 'error';
  // `badgeClass` na Portalech barvi presne tyhle tri stavy oranzove = ceka se.
  if (status === 'QUEUED' || status === 'VALIDATED' || status === 'NOT_PUBLISHED' || status === 'DRAFT') {
    return 'waiting';
  }
  return 'off';
}

export interface PortalExportRow {
  readonly descriptor: PortalDescriptor;
  /** `undefined` = portal, ke kteremu matice radek nema. */
  readonly cell: SyncCenterCell | undefined;
  readonly runState: PortalRunState;
}

export interface PortalExportSummary {
  /** Kolik portalu inzerat ZIVE bezi. */
  readonly published: number;
  /** Kam jde dnes zverejnit (jmenovatel „3/10"). */
  readonly total: number;
  readonly rows: readonly PortalExportRow[];
}

export function portalExportSummary(input: {
  /** Portaly s hotovym konektorem — `connectable` na Portalech. */
  readonly portals: readonly PortalDescriptor[];
  readonly cellMap: ReadonlyMap<string, SyncCenterCell>;
  readonly listingId: string;
}): PortalExportSummary {
  const rows: PortalExportRow[] = input.portals.map((descriptor) => {
    const cell = input.cellMap.get(`${input.listingId}:${descriptor.portal}`);
    return { descriptor, cell, runState: portalRunState(cell?.status) };
  });
  return {
    published: rows.filter((row) => row.runState === 'running').length,
    total: rows.filter((row) => canPublishToPortal(row.descriptor)).length,
    rows,
  };
}

/** Klic matice: `listingId:portal` (tentyz tvar jako na Portalech). */
export function portalCellMap(cells: readonly SyncCenterCell[]): ReadonlyMap<string, SyncCenterCell> {
  return new Map(cells.map((cell) => [`${cell.listingId}:${cell.portal}`, cell]));
}

/**
 * Souhrn pro KAZDY RADEK seznamu nemovitosti — klic je `propertyId`.
 *
 * Nemovitost bez inzeratu v mape NENI (misto „0/10" ukaze obrazovka prazdny
 * stav „Bez inzerátu"): nula zverejneni a „inzerat jeste nevznikl" jsou pro
 * maklere dve ruzne veci. Inzerat bez vazby na nemovitost (demo fixtura) se
 * preskakuje.
 */
export function portalExportByProperty(
  view: SyncCenterView,
  portals: readonly PortalDescriptor[],
): ReadonlyMap<string, PortalExportSummary> {
  /*
   * Tolerantni cteni odpovedi: atrapa, ktera vrati `{ items: [] }` misto
   * `{ listings, cells }` (konvence snimkovych sad adminu), nesmi shodit
   * CELY seznam nemovitosti — sloupec zustane prazdny. Poucenim z E2E atrap
   * („pas administrace si sam tahne…", `uxd1b-seznamy-snimky.spec.ts`).
   */
  const cellMap = portalCellMap(Array.isArray(view.cells) ? view.cells : []);
  const byProperty = new Map<string, PortalExportSummary>();
  for (const listing of Array.isArray(view.listings) ? view.listings : []) {
    const propertyId = listing.propertyId ?? null;
    if (propertyId === null || propertyId === '') continue;
    const summary = portalExportSummary({ portals, cellMap, listingId: listing.listingId });
    const previous = byProperty.get(propertyId);
    /*
     * Jedna nemovitost muze mit v matici vic inzeratu (koncept + odeslany).
     * Radek seznamu je jeden, takze se bere ten, ktery bezi na vic portalech
     * — makler se pta „inzeruje se to?", ne „kolik mam konceptu".
     */
    if (previous === undefined || summary.published > previous.published) byProperty.set(propertyId, summary);
  }
  return byProperty;
}
